import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';

/* ── Data ────────────────────────────────────────────────────────── */

const ROI = [
  { value: '80%',   label: 'Faster shift filling',   sub: 'vs manual coordination' },
  { value: '90%',   label: 'Less manual calling',     sub: 'save hours every shift' },
  { value: '24/7',  label: 'Automated coverage',      sub: 'nights, weekends, holidays' },
  { value: '4 min', label: 'Avg fill time',           sub: 'report to confirmation' },
];

const BENEFITS = [
  { emoji: '⚡', tint: '#F6E7E1', title: 'Reduce Staffing Gaps',    desc: 'AI fills open shifts in minutes — preventing care disruptions before they happen.' },
  { emoji: '⏱️', tint: '#EAEDE2', title: 'Save Admin Time',          desc: 'Eliminate manual calling. Nurses focus on care, not coordination.' },
  { emoji: '🔔', tint: '#F0EEE7', title: 'Real-Time Notifications',  desc: 'Staff receive instant alerts via SMS, Email, Teams and mobile push.' },
  { emoji: '✓',  tint: '#EAEDE2', title: 'Automated Allocation',     desc: 'First qualified staff member to accept is automatically assigned and confirmed.' },
  { emoji: '🛡️', tint: '#F6E7E1', title: 'Compliance Tracking',      desc: 'Every action logged with timestamps for auditing and regulatory reporting.' },
  { emoji: '📊', tint: '#F0EEE7', title: 'Workforce Analytics',      desc: 'Spot patterns in sick leave, response rates, and staffing coverage.' },
];

const WORKFLOW = [
  { step: '01', emoji: '📞', label: 'Staff Calls Sick',         desc: "HCA reports they can't attend" },
  { step: '02', emoji: '⚠️', label: 'Nurse Reports Absence',    desc: 'Coordinator logs the sick leave' },
  { step: '03', emoji: '👥', label: 'AI Finds Staff',           desc: 'Scans qualified, available HCAs' },
  { step: '04', emoji: '📬', label: 'Notifications Sent',       desc: 'SMS, Email & Teams instantly' },
  { step: '05', emoji: '✅', label: 'Staff Accepts',             desc: 'First eligible responder assigned' },
  { step: '06', emoji: '📋', label: 'Roster Updated',           desc: 'All systems sync in real-time' },
  { step: '07', emoji: '💬', label: 'Nurse Confirmed',          desc: 'Coordinator gets confirmation' },
];

const DB_STATS = [
  { label: 'Open Shifts',     value: '3',   color: '#C0492A' },
  { label: 'Filled Today',    value: '8',   color: '#5E6B39' },
  { label: 'Staff Available', value: '47',  color: '#2A6FB0' },
  { label: 'Response Rate',   value: '94%', color: '#5E6B39' },
  { label: 'Sick Leave',      value: '2',   color: '#C0492A' },
  { label: 'Notifications',   value: '12',  color: '#8A6BB0' },
];

const SHIFT_REQUESTS = [
  { sick: 'Sarah Chen',  time: '7:00 AM – 3:00 PM',  assigned: 'John Maka', status: 'filled' },
  { sick: 'Mark Wilson', time: '3:00 PM – 11:00 PM', assigned: '—',         status: 'notified' },
  { sick: 'Lisa Park',   time: '7:00 AM – 3:00 PM',  assigned: '—',         status: 'open' },
];

const STAFF = [
  { name: 'John Maka',    qual: 'HCA Level 2', dist: '2.3 km', status: 'assigned' },
  { name: 'Maria Santos', qual: 'HCA Level 1', dist: '4.1 km', status: 'ready' },
  { name: 'James Tane',   qual: 'HCA Level 2', dist: '—',      status: 'unavailable' },
  { name: 'Priya Singh',  qual: 'HCA Level 2', dist: '1.8 km', status: 'ready' },
  { name: 'Emma Liu',     qual: 'RN',          dist: '3.2 km', status: 'ready' },
];

const TIMELINE = [
  { time: '08:00', event: 'Sarah Chen reported sick',        detail: 'Morning shift',              dot: '#C0492A' },
  { time: '08:01', event: 'AI identified 12 eligible HCAs', detail: 'Matched by qualification',   dot: '#5E6B39' },
  { time: '08:02', event: 'Notifications dispatched',        detail: 'SMS, Email & Teams',         dot: '#2A6FB0' },
  { time: '08:04', event: 'Maria S. declined',               detail: 'Unavailable today',          dot: '#B3AEA3' },
  { time: '08:05', event: 'John Maka accepted ✓',            detail: 'Confirmed for morning shift', dot: '#5E6B39' },
  { time: '08:06', event: 'Roster updated',                  detail: 'Dashboard synchronized',     dot: '#5E6B39' },
];

const TECH = [
  { emoji: '💬', label: 'Microsoft Teams' },
  { emoji: '📱', label: 'SMS Integration' },
  { emoji: '✉️', label: 'Email Automation' },
  { emoji: '🔔', label: 'Push Notifications' },
  { emoji: '⚡', label: 'AI Matching Engine' },
  { emoji: '📊', label: 'Real-Time Dashboard' },
  { emoji: '🛡️', label: 'Audit Logs' },
  { emoji: '📈', label: 'Power BI Analytics' },
  { emoji: '🗄️', label: 'Workforce Reporting' },
  { emoji: '🔒', label: 'HIPAA Compliant' },
  { emoji: '🌐', label: 'API Integrations' },
  { emoji: '👁️', label: 'Live Monitoring' },
];

const DEMO_RESPONSES = [
  { name: 'Lisa K.',   action: 'viewed',   delay: 800 },
  { name: 'Mark D.',   action: 'declined', delay: 1600 },
  { name: 'Maria S.',  action: 'declined', delay: 2400 },
  { name: 'John Maka', action: 'accepted', delay: 3200 },
];

const REPLACEMENTS = ['John Maka', 'Maria Santos', 'Priya Singh', 'Emma Liu'];

/* ── Helpers ─────────────────────────────────────────────────────── */

const BADGE_MAP = {
  filled:      { label: 'Filled',    bg: '#E8EBDD', color: '#5E6B39' },
  assigned:    { label: 'Assigned',  bg: '#E8EBDD', color: '#5E6B39' },
  notified:    { label: 'Notified',  bg: '#E3EDF5', color: '#2A6FB0' },
  ready:       { label: 'Ready',     bg: '#E3EDF5', color: '#2A6FB0' },
  open:        { label: 'Open',      bg: '#F6E2DB', color: '#C0492A' },
  unavailable: { label: 'On Leave',  bg: '#ECEAE3', color: '#8A857A' },
};

const Badge = ({ status }) => {
  const b = BADGE_MAP[status] || BADGE_MAP.open;
  return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 9px', borderRadius: 20, background: b.bg, color: b.color }}>
      {b.label}
    </span>
  );
};

/* ── Dashboard ───────────────────────────────────────────────────── */

function Dashboard() {
  return (
    <div style={{ background: '#fff', borderRadius: 22, border: '1px solid #E6E2D8', boxShadow: '0 18px 50px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
      {/* Topbar */}
      <div style={{ background: '#1A1A18', padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 15, fontWeight: 800, color: '#fff' }}>
          🤖 Custodian AI Rostering <span style={{ fontWeight: 500, color: '#8A857A', fontSize: 13 }}>· Auckland Care Residence</span>
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12, fontWeight: 600, color: '#8FA86A' }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#8FA86A', display: 'inline-block' }} />
          Live
        </span>
      </div>

      <div style={{ padding: '22px 24px' }}>
        {/* Stat row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 10, marginBottom: 20 }}>
          {DB_STATS.map(s => (
            <div key={s.label} style={{ background: '#F6F4EE', borderRadius: 11, padding: 14 }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#8A857A', marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Main grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr', gap: 16 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Shift Requests */}
            <div style={{ border: '1px solid #EAE6DC', borderRadius: 12, overflow: 'hidden' }}>
              <div style={{ background: '#F6F4EE', padding: '10px 14px', fontSize: 12, fontWeight: 800, color: '#3A3730' }}>📅 Live Shift Requests</div>
              {SHIFT_REQUESTS.map(r => (
                <div key={r.sick} style={{ display: 'grid', gridTemplateColumns: '1fr 1.1fr 1fr auto', gap: 8, alignItems: 'center', padding: '11px 14px', borderTop: '1px solid #F0ECE2', fontSize: 12 }}>
                  <span style={{ fontWeight: 600, color: '#3A3730' }}>{r.sick}</span>
                  <span style={{ color: '#8A857A' }}>{r.time}</span>
                  <span style={{ color: '#3A3730' }}>{r.assigned}</span>
                  <Badge status={r.status} />
                </div>
              ))}
            </div>

            {/* Staff */}
            <div style={{ border: '1px solid #EAE6DC', borderRadius: 12, overflow: 'hidden' }}>
              <div style={{ background: '#F6F4EE', padding: '10px 14px', fontSize: 12, fontWeight: 800, color: '#3A3730' }}>👥 Available Staff</div>
              {STAFF.map(s => (
                <div key={s.name} style={{ display: 'grid', gridTemplateColumns: '1fr 0.9fr 0.7fr auto', gap: 8, alignItems: 'center', padding: '10px 14px', borderTop: '1px solid #F0ECE2', fontSize: 12 }}>
                  <span style={{ fontWeight: 600, color: '#3A3730' }}>{s.name}</span>
                  <span style={{ color: '#8A857A' }}>{s.qual}</span>
                  <span style={{ color: '#8A857A' }}>{s.dist}</span>
                  <Badge status={s.status} />
                </div>
              ))}
            </div>
          </div>

          {/* Timeline */}
          <div style={{ border: '1px solid #EAE6DC', borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ background: '#F6F4EE', padding: '10px 14px', fontSize: 12, fontWeight: 800, color: '#3A3730' }}>⚡ Activity Timeline</div>
            <div style={{ padding: 14 }}>
              {TIMELINE.map((t, i) => (
                <div key={i} style={{ display: 'flex', gap: 11, paddingBottom: 14 }}>
                  <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <span style={{ width: 9, height: 9, borderRadius: '50%', background: t.dot, marginTop: 3, display: 'inline-block' }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#1A1A18' }}>{t.event}</div>
                    <div style={{ fontSize: 11, color: '#8A857A', marginTop: 1 }}>{t.time} · {t.detail}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Interactive Demo ────────────────────────────────────────────── */

const SHIFT_LABELS = { morning: '7:00 AM – 3:00 PM', afternoon: '3:00 PM – 11:00 PM', night: '11:00 PM – 7:00 AM' };
const LOCATION_LABELS = { sunrise: 'Sunrise Care Auckland', meadowbank: 'Meadowbank Aged Care', northshore: 'North Shore Residential' };

function InteractiveDemo() {
  const [form, setForm] = useState({ staffName: '', shiftTime: 'morning', location: 'sunrise' });
  const [step, setStep] = useState(0);
  const [responses, setResponses] = useState([]);
  const [replacement, setReplacement] = useState('John Maka');

  const runDemo = () => {
    if (!form.staffName.trim()) return;
    const rep = REPLACEMENTS[Math.floor(Math.random() * REPLACEMENTS.length)];
    setStep(1); setResponses([]); setReplacement(rep);
    [800, 1600, 2400, 3200, 5000, 6000, 7000].forEach((d, i) => setTimeout(() => setStep(i + 2), d));
    DEMO_RESPONSES.forEach(r => setTimeout(() => setResponses(prev => [...prev, r]), 3200 + r.delay));
  };

  const reset = () => { setStep(0); setResponses([]); setForm({ staffName: '', shiftTime: 'morning', location: 'sunrise' }); };

  const isRunning = step > 0 && step < 8;
  const isDone = step >= 8;

  const demoSteps = [
    { label: 'Recording absence',       detail: `Logging sick leave for ${form.staffName || 'staff member'}` },
    { label: 'Scanning staff database', detail: 'Checking 47 qualified staff profiles…' },
    { label: '12 eligible HCAs found',  detail: 'Matched by qualification, availability & proximity' },
    { label: 'Sending notifications',   detail: 'SMS, Email & Teams dispatched to 12 staff' },
    { label: 'Collecting responses',    detail: 'Monitoring replies in real-time' },
    { label: `${replacement} accepted!`, detail: `Shift confirmed: ${SHIFT_LABELS[form.shiftTime]}` },
    { label: 'Roster updated',          detail: 'All systems synchronized · Nurse notified' },
  ];

  const inputStyle = { width: '100%', padding: '11px 13px', border: '1.5px solid #DBD6CC', borderRadius: 10, fontSize: 14, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' };
  const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, color: '#4A463E', marginBottom: 6 };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '0.85fr 1.15fr', background: '#fff', borderRadius: 22, boxShadow: '0 12px 40px rgba(0,0,0,0.07)', overflow: 'hidden', minHeight: 440 }}>
      {/* Form */}
      <div style={{ padding: 32, borderRight: '1px solid #EFEBE1' }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: '#C0492A', marginBottom: 22 }}>⚠ Report Staff Sick Leave</div>

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Staff Name *</label>
          <input value={form.staffName} onChange={e => setForm(p => ({ ...p, staffName: e.target.value }))}
            placeholder="e.g. Sarah Chen" disabled={isRunning || isDone} style={inputStyle} />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Shift Time</label>
          <select value={form.shiftTime} onChange={e => setForm(p => ({ ...p, shiftTime: e.target.value }))}
            disabled={isRunning || isDone} style={{ ...inputStyle, background: '#fff', cursor: 'pointer' }}>
            <option value="morning">Morning (7:00 AM – 3:00 PM)</option>
            <option value="afternoon">Afternoon (3:00 PM – 11:00 PM)</option>
            <option value="night">Night (11:00 PM – 7:00 AM)</option>
          </select>
        </div>

        <div style={{ marginBottom: 22 }}>
          <label style={labelStyle}>Facility</label>
          <select value={form.location} onChange={e => setForm(p => ({ ...p, location: e.target.value }))}
            disabled={isRunning || isDone} style={{ ...inputStyle, background: '#fff', cursor: 'pointer' }}>
            <option value="sunrise">Sunrise Care Auckland</option>
            <option value="meadowbank">Meadowbank Aged Care</option>
            <option value="northshore">North Shore Residential</option>
          </select>
        </div>

        <button onClick={isDone ? reset : runDemo} disabled={isRunning || (!isDone && !form.staffName.trim())}
          style={{ width: '100%', border: 'none', borderRadius: 11, padding: 14, fontFamily: 'inherit', fontSize: 15, fontWeight: 700, cursor: isRunning ? 'default' : 'pointer', background: isDone ? '#1A1A18' : '#C0492A', color: '#fff', opacity: isRunning ? 0.7 : 1 }}>
          {isRunning ? 'Running…' : isDone ? '↺ Try Again' : '▶ Start Simulation'}
        </button>

        {step >= 6 && (
          <div style={{ display: 'flex', gap: 11, alignItems: 'center', background: '#EDF0E5', borderRadius: 12, padding: '14px 16px', marginTop: 18 }}>
            <span style={{ fontSize: 22 }}>✅</span>
            <div>
              <div style={{ fontSize: 14, fontWeight: 800, color: '#4A5A2E' }}>Shift filled in ~4 minutes</div>
              <div style={{ fontSize: 12, color: '#6E7A52' }}>{LOCATION_LABELS[form.location]} · {SHIFT_LABELS[form.shiftTime]}</div>
            </div>
          </div>
        )}
      </div>

      {/* Simulation */}
      <div style={{ padding: 32, background: '#FBFAF7' }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: '#8A857A', letterSpacing: '0.05em', marginBottom: 18 }}>⚡ LIVE SIMULATION</div>

        {step === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 320, color: '#B3AEA3', textAlign: 'center' }}>
            <div style={{ fontSize: 44, marginBottom: 14 }}>⚡</div>
            <p style={{ fontSize: 15, margin: 0 }}>Fill in the form and click<br /><strong style={{ color: '#8A857A' }}>Start Simulation</strong></p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {demoSteps.map((s, i) => {
              const done = step > i + 1;
              const active = step === i + 1;
              return (
                <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', opacity: (active || done) ? 1 : 0.45 }}>
                  <div style={{ flexShrink: 0, width: 26, height: 26, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, background: done ? '#5E6B39' : active ? '#C0492A' : '#ECEAE3', color: (done || active) ? '#fff' : '#8A857A' }}>
                    {done ? '✓' : String(i + 1)}
                  </div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: (active || done) ? '#1A1A18' : '#9A958A' }}>{s.label}</div>
                    {(active || done) && <div style={{ fontSize: 12, color: '#8A857A', marginTop: 1 }}>{s.detail}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {responses.length > 0 && (
          <div style={{ marginTop: 18, borderTop: '1px solid #EFEBE1', paddingTop: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: '#8A857A', marginBottom: 10 }}>STAFF RESPONSES</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              {responses.map((r, i) => {
                const map = {
                  viewed:   { label: 'viewed',     bg: '#F6F4EE', color: '#8A857A' },
                  declined: { label: 'declined',   bg: '#F6F4EE', color: '#A0654E' },
                  accepted: { label: '✓ accepted', bg: '#E8EBDD', color: '#5E6B39' },
                };
                const c = map[r.action];
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderRadius: 9, background: c.bg }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#3A3730' }}>{r.name}</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: c.color }}>{c.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Page ────────────────────────────────────────────────────────── */

const RosteringPage = () => (
  <div style={{ minHeight: '100vh', background: '#ECEAE3', fontFamily: "'Inter', sans-serif", color: '#1A1A18', backgroundImage: 'linear-gradient(rgba(0,0,0,0.022) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.022) 1px, transparent 1px)', backgroundSize: '44px 44px' }}>
    <Header />

    {/* ── Hero ── */}
    <section style={{ maxWidth: 1100, margin: '0 auto', padding: '72px 40px 0', textAlign: 'center' }}>
      <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: '0.1em', color: '#5E6B39', marginBottom: 18 }}>HEALTHCARE STAFFING AUTOMATION</div>
      <h1 style={{ fontSize: 'clamp(2.4rem, 5vw, 58px)', fontWeight: 800, letterSpacing: '-0.03em', margin: 0, lineHeight: 1.04 }}>
        Fill open shifts in<br />
        <span style={{ borderBottom: '6px solid #C0492A', paddingBottom: 2 }}>minutes, not hours.</span>
      </h1>
      <p style={{ fontSize: 20, lineHeight: 1.55, color: '#5A564D', maxWidth: 640, margin: '22px auto 0' }}>
        When staff call in sick, Custodian AI automatically finds available healthcare workers, sends shift offers, and updates your roster — without a single phone call.
      </p>
      <div style={{ display: 'flex', gap: 14, justifyContent: 'center', marginTop: 32, flexWrap: 'wrap' }}>
        <a href="#ro-demo" style={{ display: 'inline-block', background: '#C0492A', color: '#fff', fontSize: 16, fontWeight: 700, padding: '15px 28px', borderRadius: 12, textDecoration: 'none' }}>
          ▶ Try the interactive demo
        </a>
        <a href="/#booking" style={{ display: 'inline-block', border: '1.5px solid #1A1A18', color: '#1A1A18', fontSize: 16, fontWeight: 700, padding: '15px 28px', borderRadius: 12, textDecoration: 'none' }}>
          Book a demo →
        </a>
      </div>
      {/* ROI stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, maxWidth: 820, margin: '52px auto 0' }}>
        {ROI.map(r => (
          <div key={r.value} style={{ background: '#fff', borderRadius: 18, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <div style={{ fontSize: 34, fontWeight: 800, letterSpacing: '-0.02em', color: '#C0492A' }}>{r.value}</div>
            <div style={{ fontSize: 14, fontWeight: 700, marginTop: 4 }}>{r.label}</div>
            <div style={{ fontSize: 12, color: '#8A857A', marginTop: 2 }}>{r.sub}</div>
          </div>
        ))}
      </div>
    </section>

    {/* ── Benefits ── */}
    <section style={{ maxWidth: 1150, margin: '0 auto', padding: '88px 40px 0' }}>
      <div style={{ textAlign: 'center', marginBottom: 44 }}>
        <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: '0.1em', color: '#C0492A' }}>KEY BENEFITS</div>
        <h2 style={{ fontSize: 'clamp(1.8rem, 3vw, 38px)', fontWeight: 800, letterSpacing: '-0.025em', margin: '10px 0 0' }}>Everything your team needs to manage shifts effortlessly</h2>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }}>
        {BENEFITS.map(b => (
          <div key={b.title} style={{ background: '#fff', borderRadius: 20, padding: 30, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <div style={{ width: 50, height: 50, borderRadius: 14, background: b.tint, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, marginBottom: 16 }}>{b.emoji}</div>
            <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 700 }}>{b.title}</h3>
            <p style={{ margin: 0, fontSize: 14, lineHeight: 1.55, color: '#5A564D' }}>{b.desc}</p>
          </div>
        ))}
      </div>
    </section>

    {/* ── How It Works ── */}
    <section style={{ marginTop: 88, background: '#1A1A18', color: '#fff' }}>
      <div style={{ maxWidth: 1150, margin: '0 auto', padding: '72px 40px' }}>
        <div style={{ textAlign: 'center', marginBottom: 44 }}>
          <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: '0.1em', color: '#E0794F' }}>HOW IT WORKS</div>
          <h2 style={{ fontSize: 'clamp(1.8rem, 3vw, 38px)', fontWeight: 800, letterSpacing: '-0.025em', margin: '10px 0 0' }}>Seven steps. Fully automated.</h2>
          <p style={{ fontSize: 17, color: '#B5B0A5', margin: '14px 0 0' }}>From sick call to confirmed replacement — Custodian AI handles everything.</p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 12 }}>
          {WORKFLOW.map(w => (
            <div key={w.step} style={{ background: '#232019', border: '1px solid #2E2C28', borderRadius: 16, padding: '20px 14px', textAlign: 'center' }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: '#E0794F', marginBottom: 10 }}>{w.step}</div>
              <div style={{ fontSize: 24, marginBottom: 10 }}>{w.emoji}</div>
              <div style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.25, marginBottom: 6 }}>{w.label}</div>
              <div style={{ fontSize: 11, lineHeight: 1.4, color: '#9A958A' }}>{w.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ── Dashboard ── */}
    <section style={{ maxWidth: 1150, margin: '0 auto', padding: '88px 40px 0' }}>
      <div style={{ textAlign: 'center', marginBottom: 36 }}>
        <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: '0.1em', color: '#C0492A' }}>LIVE DASHBOARD</div>
        <h2 style={{ fontSize: 'clamp(1.8rem, 3vw, 38px)', fontWeight: 800, letterSpacing: '-0.025em', margin: '10px 0 0' }}>Complete visibility over every shift</h2>
      </div>
      <Dashboard />
    </section>

    {/* ── Interactive Demo ── */}
    <section id="ro-demo" style={{ maxWidth: 1000, margin: '0 auto', padding: '88px 40px 0' }}>
      <div style={{ textAlign: 'center', marginBottom: 36 }}>
        <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: '0.1em', color: '#C0492A' }}>TRY IT YOURSELF</div>
        <h2 style={{ fontSize: 'clamp(1.8rem, 3vw, 38px)', fontWeight: 800, letterSpacing: '-0.025em', margin: '10px 0 0' }}>Watch the AI fill a shift in real time</h2>
        <p style={{ fontSize: 17, color: '#5A564D', margin: '14px 0 0' }}>Enter a staff name and shift, then watch Custodian AI automate the whole process.</p>
      </div>
      <InteractiveDemo />
    </section>

    {/* ── Tech ── */}
    <section style={{ maxWidth: 1000, margin: '0 auto', padding: '88px 40px 0' }}>
      <div style={{ textAlign: 'center', marginBottom: 36 }}>
        <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: '0.1em', color: '#C0492A' }}>INTEGRATIONS & FEATURES</div>
        <h2 style={{ fontSize: 'clamp(1.8rem, 3vw, 38px)', fontWeight: 800, letterSpacing: '-0.025em', margin: '10px 0 0' }}>Enterprise-grade, built for healthcare</h2>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center' }}>
        {TECH.map(t => (
          <span key={t.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 9, background: '#fff', border: '1px solid #E6E2D8', borderRadius: 999, padding: '11px 18px', fontSize: 14, fontWeight: 600, color: '#3A3730' }}>
            {t.emoji} {t.label}
          </span>
        ))}
      </div>
    </section>

    {/* ── CTA ── */}
    <section style={{ maxWidth: 1000, margin: '80px auto 0', padding: '0 40px' }}>
      <div style={{ background: '#5E6B39', color: '#fff', borderRadius: 28, padding: 56, textAlign: 'center' }}>
        <h2 style={{ fontSize: 'clamp(1.6rem, 3vw, 34px)', fontWeight: 800, letterSpacing: '-0.02em', margin: 0 }}>Ready to automate your shift management?</h2>
        <p style={{ fontSize: 18, color: '#E2E5D6', margin: '14px 0 30px' }}>Join healthcare facilities using Custodian AI to eliminate staffing stress.</p>
        <a href="/#booking" style={{ display: 'inline-block', background: '#fff', color: '#1A1A18', fontSize: 17, fontWeight: 700, padding: '16px 32px', borderRadius: 13, textDecoration: 'none' }}>
          Book a free demo →
        </a>
      </div>
    </section>

    <div style={{ marginTop: 80 }} />
    <Footer />
  </div>
);

export default RosteringPage;
